import React, { useState } from 'react';
import { CheckIcon, FileTextIcon, HistoryIcon, SparklesIcon, XIcon } from 'lucide-react';
import { conditionStatusTone, type Condition, type ConditionStatus } from '../data/loanData';
type ConditionsPanelProps = {
  conditions: Condition[];
};
function confidenceTone(score: number): string {
  if (score >= 0.85) return 'text-success';
  if (score >= 0.6) return 'text-amber-600';
  return 'text-danger';
}
function ConditionRow({
  condition
}: {
  condition: Condition;
}) {
  const [status, setStatus] = useState<ConditionStatus>(condition.status);
  const [showHistory, setShowHistory] = useState(false);
  return <li className="rounded-xl border border-gray-200 p-3.5" data-id="element-53" data-mp="22wvrdouwmpo1">
      <div className="flex items-start gap-3" data-id="element-54" data-mp="1y0f22rbyy59d">
        <div className="min-w-0 flex-1" data-id="element-55" data-mp="1xxj3d0sarcjv">
          <p className="text-[13px] font-semibold text-ink" data-id="element-56" data-mp="2vfj3ierxaucz">{condition.name}</p>
          <p className="mt-0.5 text-[11.5px] uppercase tracking-wide text-gray-400" data-id="element-57" data-mp="2qoau3iqlf8zt">
            {condition.category}
          </p>
        </div>
        <span className={['shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium', conditionStatusTone[status]].join(' ')} data-id="element-58" data-mp="11ssnzl0mnqbk">
          {status}
        </span>
      </div>

      {condition.document ? <div className="mt-3 rounded-lg bg-gray-50 p-3" data-id="element-59" data-mp="x0j16dut76di">
          <div className="flex items-center gap-2" data-id="element-60" data-mp="wyyc33r9kfn1">
            <FileTextIcon className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" data-id="element-61" data-mp="i38v5k0xqsp6" />
            <span className="truncate text-[12.5px] font-medium text-ink" data-id="element-62" data-mp="n2d6ag7f70i5">
              {condition.document}
            </span>
            <span className="ml-auto shrink-0 text-[11px] text-gray-500" data-id="element-63" data-mp="93vjqlt93u1t">
              {condition.source}
            </span>
          </div>

          {typeof condition.confidence === 'number' && <div className="mt-2.5 flex items-center gap-2" data-id="element-64" data-mp="23hwf2wpldyx5">
              <SparklesIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-65" data-mp="28fo9bxdplx4i" />
              <span className="text-[11.5px] text-gray-500" data-id="element-66" data-mp="28dlkhgoama1d">Condition-type match</span>
              <span className={['text-[12px] font-semibold', confidenceTone(condition.confidence)].join(' ')} data-id="element-67" data-mp="25z6ntntqtesy">
                {Math.round(condition.confidence * 100)}%
              </span>
            </div>}

          {condition.reasoning && <p className="mt-1.5 text-[12px] leading-snug text-gray-600" data-id="element-68" data-mp="2pps833urdbj8">
              {condition.reasoning}
            </p>}

          <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-69" data-mp="2kqp83xtmxvi4">
            <button type="button" onClick={() => setStatus('Accepted')} disabled={status === 'Accepted'} className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-2.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-ink/90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500" data-id="element-70" data-mp="2fyp49uag2ig9">
              <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-71" data-mp="3b0ziekmtnztd" />
              Accept match
            </button>
            <button type="button" onClick={() => setStatus('Rejected')} disabled={status === 'Rejected'} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] font-medium text-ink transition-colors hover:bg-white disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-72" data-mp="3g02br7tfwhli">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-73" data-mp="2rz8tmx7co6x" />
              Unassociate
            </button>
            <button type="button" onClick={() => setShowHistory(prev => !prev)} aria-expanded={showHistory} className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-brand hover:text-brand-dark" data-id="element-74" data-mp="7ksdybwl1yjj">
              <HistoryIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-75" data-mp="lka8l7wk860t" />
              AI history ({condition.history.length})
            </button>
          </div>

          {showHistory && <ul className="mt-3 space-y-1.5 border-t border-gray-200 pt-2.5" data-id="element-76" data-mp="1e9trb5wmk514">
              {condition.history.map(run => <li key={run.id} className="flex items-baseline gap-2 text-[11.5px]" data-id="element-77" data-mp="1rzxafo07jimc">
                  <span className="w-28 shrink-0 text-gray-400" data-id="element-78" data-mp="1o04xkz3s6qb6">{run.timestamp}</span>
                  <span className="text-gray-600" data-id="element-79" data-mp="10aalijrad5rz">{run.outcome}</span>
                </li>)}
              {condition.history.length === 0 && <li className="text-[11.5px] text-gray-400" data-id="element-80" data-mp="199xoh9znyjn5">No AI runs yet.</li>}
            </ul>}
        </div> : <p className="mt-3 rounded-lg border border-dashed border-gray-200 px-3 py-2.5 text-[12px] text-gray-500" data-id="element-81" data-mp="149t95nahruyi">
          No document matched yet — borrower portal uploads are analyzed automatically.
        </p>}
    </li>;
}
export function ConditionsPanel({
  conditions
}: ConditionsPanelProps) {
  const [autoRelabel, setAutoRelabel] = useState(false);
  const matched = conditions.filter(condition => condition.status === 'AI Matched').length;
  return <div className="space-y-4" data-id="element-82" data-mp="2e942xirsdgtq">
      <div className="rounded-xl border border-brand/30 bg-brand-soft/60 p-3.5" data-id="element-83" data-mp="2ea5ncduozfo4">
        <div className="flex items-start gap-2.5" data-id="element-84" data-mp="2j75ku0dh7a3m">
          <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-85" data-mp="2ja1jjqx5e2t4" />
          <div className="min-w-0" data-id="element-86" data-mp="36zmcn2n0mgg4">
            <p className="text-[13px] font-semibold text-ink" data-id="element-87" data-mp="3bx4cm3j21lbz">Document intelligence</p>
            <p className="mt-0.5 text-[12px] leading-snug text-gray-600" data-id="element-88" data-mp="2x7q570z1hdz8">
              {matched} document{matched === 1 ? '' : 's'} matched to conditions and waiting
              on your review. Confidence reflects match to the condition type, not whether
              the condition is satisfied.
            </p>
          </div>
        </div>

        <label className="mt-3 flex cursor-pointer items-center gap-3 rounded-lg bg-white px-3 py-2.5" data-id="element-89" data-mp="3unvslnxjs9zc">
          <input type="checkbox" className="sr-only" checked={autoRelabel} onChange={() => setAutoRelabel(prev => !prev)} data-id="element-90" data-mp="8d6k4b76id77" />
          <span aria-hidden="true" className={['relative h-5 w-9 shrink-0 rounded-full transition-colors', autoRelabel ? 'bg-brand' : 'bg-gray-300'].join(' ')} data-id="element-91" data-mp="3ujveyhus2xz8">
            <span className={['absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all', autoRelabel ? 'left-[18px]' : 'left-0.5'].join(' ')} data-id="element-92" data-mp="3lmbbr3dcurlz" />
          </span>
          <span className="min-w-0" data-id="element-93" data-mp="36vu7lc9c5nle">
            <span className="block text-[12.5px] font-medium text-ink" data-id="element-94" data-mp="36wx1lt1yg65j">
              Auto-relabel manual uploads
            </span>
            <span className="block text-[11.5px] text-gray-500" data-id="element-95" data-mp="2xunr336oscs5">
              Off by default. Borrower portal uploads are always processed.
            </span>
          </span>
        </label>
      </div>

      <ul className="space-y-2.5" data-id="element-96" data-mp="1iboe5pnxm9ts">
        {conditions.map(condition => <ConditionRow key={condition.id} condition={condition} data-id="element-97" data-mp="1idss70fjop7s" />)}
      </ul>
    </div>;
}